import { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles, Loader2, Check, FlaskConical, Wand2 } from 'lucide-react';
import { writeCampaignWithAI, type CampaignAiOption, type EmailOption, type SmsOption } from '../../lib/marketingAI';

interface AICampaignWriterModalProps {
  channel: 'email' | 'sms';
  businessId: string;
  businessName?: string;
  logoUrl?: string | null;
  websiteUrlFallback?: string | null;
  businessSlug?: string | null;
  onClose: () => void;
  onUseOne: (option: CampaignAiOption) => void;
  onUseAll: (options: CampaignAiOption[]) => void;
}

const TONES = ['Friendly', 'Professional', 'Urgent', 'Playful', 'Luxury'];

export default function AICampaignWriterModal({
  channel,
  businessId,
  businessName,
  logoUrl,
  websiteUrlFallback,
  businessSlug,
  onClose,
  onUseOne,
  onUseAll,
}: AICampaignWriterModalProps) {
  const [prompt, setPrompt] = useState('');
  const [tone, setTone] = useState<string>('Friendly');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [options, setOptions] = useState<CampaignAiOption[]>([]);

  const generate = async () => {
    if (!prompt.trim()) { setError('Tell the AI what this campaign is about.'); return; }
    setError(null);
    setLoading(true);
    try {
      const opts = await writeCampaignWithAI({
        channel,
        prompt,
        businessId,
        businessName,
        tone,
        logoUrl,
        websiteUrlFallback,
        businessSlug,
      });
      setOptions(opts);
    } catch (e: any) {
      setError(e?.message || 'Failed to generate. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <div
      className="fixed inset-0 z-[100050] bg-black/50 flex items-center justify-center p-4"
      // Tall content can reach the top edge — keep it clear of the iOS notch & home indicator
      style={{
        paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)',
        paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 1rem)',
      }}
      onClick={loading ? undefined : onClose}
    >
      <div className="bg-gray-100 rounded-2xl shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-white rounded-t-2xl shrink-0">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-violet-50"><Sparkles className="h-4 w-4 text-violet-600" /></div>
            <div>
              <div className="text-[12px] font-semibold uppercase tracking-wide text-gray-400">AI Marketing Assistant</div>
              <div className="text-[15px] font-semibold text-gray-900">Write {channel === 'sms' ? 'an SMS' : 'an email'} campaign</div>
            </div>
          </div>
          <button onClick={onClose} disabled={loading} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 disabled:opacity-50"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
          {/* Prompt */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <div>
              <label className="block text-[13px] font-semibold text-gray-800 mb-1.5">What's this campaign about?</label>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="e.g. Spring detailing special — 20% off full interior + exterior packages, book by March 31. Target past customers who haven't booked in 3+ months."
                rows={3}
                className="w-full text-[14px] border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500/30 resize-y"
                style={{ fontSize: '16px' }}
              />
            </div>
            <div>
              <label className="block text-[12px] font-medium text-gray-500 mb-1.5">Tone</label>
              <div className="flex flex-wrap gap-1.5">
                {TONES.map((t) => (
                  <button
                    key={t}
                    onClick={() => setTone(t)}
                    className={`text-[12px] px-2.5 py-1 rounded-full border transition-colors ${tone === t ? 'bg-violet-600 text-white border-transparent' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={generate}
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-violet-600 text-white text-[14px] font-semibold hover:bg-violet-700 disabled:opacity-60"
            >
              {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Writing 3 options…</> : <><Wand2 className="h-4 w-4" /> {options.length ? 'Regenerate' : 'Generate 3 options'}</>}
            </button>
            {error && <div className="text-[13px] text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          </div>

          {/* Options */}
          {options.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-semibold text-gray-700">{options.length} options</span>
                <button
                  onClick={() => onUseAll(options)}
                  className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-violet-700 bg-violet-50 hover:bg-violet-100 px-2.5 py-1.5 rounded-lg"
                >
                  <FlaskConical className="h-3.5 w-3.5" /> Use all 3 as split test
                </button>
              </div>
              {options.map((opt, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-gray-900 text-white text-[11px] font-bold">{String.fromCharCode(65 + i)}</span>
                    <button
                      onClick={() => onUseOne(opt)}
                      className="inline-flex items-center gap-1 text-[12px] font-semibold text-blue-600 hover:text-blue-800"
                    >
                      <Check className="h-3.5 w-3.5" /> Use this
                    </button>
                  </div>
                  {channel === 'email' ? (
                    <div>
                      <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-0.5">Subject</div>
                      <div className="text-[14px] font-semibold text-gray-900 mb-2">{(opt as EmailOption).subject}</div>
                      <div className="text-[11px] text-gray-400 uppercase tracking-wide mb-0.5">Body</div>
                      <div className="text-[13px] text-gray-700 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: (opt as EmailOption).body }} />
                    </div>
                  ) : (
                    <div className="text-[14px] text-gray-800 whitespace-pre-wrap">{(opt as SmsOption).message}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
